import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from '@mui/material';
import dayjs from 'dayjs';

import {
	resumeSubscription,
	SubscriptionActionTypes,
	useBusinessLayer,
	useDTicketSubscriptionOffering,
	usePaymentMethod,
	useSubscription,
} from '@mopla/business-logic';
import { ESubscriptionStatus } from '@mopla/data-models';
import { passengerThemeV2 } from '@mopla/mui';
import {
	BlackOverlay,
	ErrorAnim,
	MplButton,
	MplContentLoader,
	MplModal,
} from '@mopla/ui';
import { getDTicketType, isAfterTenthOfMonth } from '@mopla/utils';

import { pushSubscriptionManagementEvent } from '../../../app/userTracking/events';
import { usePassengerAppNavigate } from '../../../hooks';

import { STEPS, STEPS_TOTAL } from './constants';
import { DTicketResumeSubscriptionBottomSheet } from './DTicketResumeSubscriptionBottomSheet';
import { DTicketResumeSubscriptionLayout } from './DTicketResumeSubscriptionLayout';
import { DTicketResumeSubscriptionSuccessStep } from './DTicketResumeSubscriptionSuccessStep';
import { Step1ResumeSubscription } from './Step1ResumeSubscription';
import { Step2ResumeSubscription } from './Step2ResumeSubscription';
import { ESubscribeStep } from './types';

export const DTicketResumeSubscription = () => {
	const { t } = useTranslation('dticketResumeSubscription');
	const bl = useBusinessLayer();

	const { navigateToUserDTicket, navigateToSubscriptionEditPause } =
		usePassengerAppNavigate();

	const [activeStep, setActiveStep] = useState<ESubscribeStep>(
		ESubscribeStep.SubscriptionInfo
	);
	const [isError, setIsError] = useState(false);
	const [isResuming, setIsResuming] = useState(false);
	const [isAcceptTariffs, setIsAcceptTariffs] = useState(false);
	const [isAcceptEarlyServiceStart, setIsAcceptEarlyServiceStart] =
		useState(false);

	const { subscriptionData: subscription, hasLoaded } = useSubscription();
	const { defaultPaymentMethod, refetchPaymentMethod, isPaymentMethodLoading } =
		usePaymentMethod();

	const { subscriptionOffering } = useDTicketSubscriptionOffering();
	const isPriceZero = subscriptionOffering?.price === 0;
	const dTicketType = getDTicketType(subscriptionOffering?.subscriptionType);

	const hasPause = Boolean(subscription?.pauseStart && subscription?.pauseEnd);
	const isActivePause = subscription?.status === ESubscriptionStatus.PAUSED;

	// Redirect if account has no pause
	useEffect(() => {
		if (!hasLoaded) return;
		if (!hasPause && activeStep === ESubscribeStep.SubscriptionInfo) {
			navigateToUserDTicket();
		}
	}, [hasLoaded, hasPause, activeStep, navigateToUserDTicket]);

	const activeStepIndex =
		activeStep === ESubscribeStep.Success
			? STEPS.length - 1
			: STEPS.findIndex((step) => step === activeStep);

	const isSubscriptionInfoStep = activeStep === ESubscribeStep.SubscriptionInfo;
	const isSummaryStep = activeStep === ESubscribeStep.Summary;
	const isSuccessStep = activeStep === ESubscribeStep.Success;

	const startDate = useMemo(() => {
		return dayjs().startOf('month');
	}, []);

	const formattedStartDate = startDate.format('DD. MMMM YYYY');

	// Validity warning: show when current month is selected, we're past the 15th, and pause is ACTIVE
	const showValidityWarning = useMemo(() => {
		if (!isActivePause) return false;

		return isAfterTenthOfMonth(startDate);
	}, [startDate, isActivePause]);

	const daysRemaining = useMemo(() => {
		return dayjs().endOf('month').diff(dayjs(), 'day') + 1;
	}, []);

	const handleResumeSubscription = useCallback((): Promise<void> => {
		return new Promise((resolve, reject) => {
			bl.dispatch(
				resumeSubscription({
					subscriptionId: subscription?.id as string,
				})
			);

			bl.watchActions({
				types: SubscriptionActionTypes.ResumeSubscriptionResult,
				callback: (action) => {
					if (action.payload?.error) {
						reject(action.payload.error);
					} else {
						pushSubscriptionManagementEvent('resumeSubscription', {});
						resolve();
					}
				},
			});
		});
	}, [bl, subscription?.id]);

	const nextStep = useCallback(() => {
		const nextIndex = activeStepIndex + 1;
		const maxIndex = STEPS.length - 1;
		setActiveStep(nextIndex > maxIndex ? STEPS[maxIndex] : STEPS[nextIndex]);
	}, [activeStepIndex]);

	const nextStepHandler = async () => {
		if (isSubscriptionInfoStep) {
			return nextStep();
		}

		// Summary step -> Resume subscription
		try {
			if (isResuming) return;

			setIsResuming(true);

			await handleResumeSubscription();

			setActiveStep(ESubscribeStep.Success);
		} catch (e) {
			console.error('Error on resuming subscription: ', e);
			setIsError(true);
		} finally {
			setIsResuming(false);
		}
	};

	const previousStepHandler = useCallback(() => {
		const prevIndex = activeStepIndex - 1;

		if (prevIndex < 0) {
			return navigateToSubscriptionEditPause();
		}

		// Reset acceptance states when going back from summary
		if (isSummaryStep) {
			setIsAcceptTariffs(false);
			setIsAcceptEarlyServiceStart(false);
		}

		return setActiveStep(STEPS[prevIndex]);
	}, [activeStepIndex, navigateToSubscriptionEditPause, isSummaryStep]);

	const handleErrorRetry = useCallback(() => {
		setIsError(false);
		navigateToSubscriptionEditPause();
	}, [navigateToSubscriptionEditPause]);

	// Fetch payment method details before entering Summary step
	useEffect(() => {
		if (isSubscriptionInfoStep) {
			refetchPaymentMethod();
		}
	}, [isSubscriptionInfoStep, refetchPaymentMethod]);

	// Loading guard for redirection when account has no subscription or no pause
	if (
		activeStep === ESubscribeStep.SubscriptionInfo &&
		(!hasLoaded || !hasPause)
	) {
		return null;
	}

	const getButtonText = (): string => {
		if (activeStep === ESubscribeStep.SubscriptionInfo) {
			return t('dticketResumeSubscription:bottom_sheet.step_1_button_next');
		}

		return t('dticketResumeSubscription:bottom_sheet.step_2_button_next');
	};

	const getIsDisabled = (): boolean => {
		if (activeStep === ESubscribeStep.Summary) {
			return (
				!defaultPaymentMethod ||
				isPaymentMethodLoading ||
				!isAcceptTariffs ||
				!isAcceptEarlyServiceStart ||
				isResuming
			);
		}

		return false;
	};

	const getHeaderTitle = (): string => {
		if (activeStep === ESubscribeStep.SubscriptionInfo) {
			return t('dticketResumeSubscription:header.title_step_1');
		}

		return t('dticketResumeSubscription:header.title_step_2');
	};

	const renderStepContent = () => {
		switch (activeStep) {
			case ESubscribeStep.SubscriptionInfo:
				return (
					<Step1ResumeSubscription
						formattedStartDate={formattedStartDate}
						showValidityWarning={showValidityWarning}
						daysRemaining={daysRemaining}
						subscriptionType={dTicketType}
					/>
				);
			case ESubscribeStep.Summary:
				return isResuming ? (
					<MplContentLoader />
				) : (
					<Step2ResumeSubscription
						formattedStartDate={formattedStartDate}
						isAcceptTariffs={isAcceptTariffs}
						setIsAcceptTariffs={setIsAcceptTariffs}
						isAcceptEarlyServiceStart={isAcceptEarlyServiceStart}
						setIsAcceptEarlyServiceStart={setIsAcceptEarlyServiceStart}
						isPriceZero={isPriceZero}
					/>
				);
			default:
				return null;
		}
	};

	return (
		<ThemeProvider theme={passengerThemeV2}>
			<BlackOverlay open={isError} />
			<MplModal
				open={isError}
				testId="DTicketResumeSubscription-MplErrorModal"
				icon={<ErrorAnim loop />}
				title={t('dticketResumeSubscription:error_modal.title')}
				buttons={
					<MplButton
						fullWidth
						onClick={handleErrorRetry}
						testId="dticket-resumeSubscription-errorModal-retry-button"
					>
						{t('dticketResumeSubscription:error_modal.button')}
					</MplButton>
				}
			/>

			<DTicketResumeSubscriptionLayout
				title={getHeaderTitle()}
				subtitle={t('dticketResumeSubscription:header.step_of', {
					step: activeStepIndex + 1,
					total: STEPS_TOTAL,
				})}
				currentStep={activeStepIndex + 1}
				totalSteps={STEPS_TOTAL}
				onBack={previousStepHandler}
				dataTestId="dticket-resume-subscription-page"
			>
				{renderStepContent()}
			</DTicketResumeSubscriptionLayout>

			<BlackOverlay open={isSuccessStep} />
			<DTicketResumeSubscriptionSuccessStep
				open={isSuccessStep}
				formattedStartDate={formattedStartDate}
				onConfirm={navigateToUserDTicket}
				isActivePause={isActivePause}
			/>

			{!isSuccessStep && !isResuming && (
				<DTicketResumeSubscriptionBottomSheet
					priceToPay={subscriptionOffering?.price}
					fullPrice={subscriptionOffering?.fullPrice}
					buttonLabel={getButtonText()}
					onButtonClick={nextStepHandler}
					isDisabled={getIsDisabled()}
					isLoading={isResuming}
				/>
			)}
		</ThemeProvider>
	);
};
